import { getDistrictMapOptions, getRequirementSubintervalsOptions, scheduleDistrictsMutation } from '@/api/main'
import type { DriveType } from '@/api/main/types.gen'
import { invalidateSchedulerQueries } from '@/routes/scheduling.$startDate.$endDate.drive.$action.$id'
import { Button, MultiSelect } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoScheduleSplit } from './AutoScheduleSplit'
import { ConfirmSummary } from './ConfirmSummary'
import { DistrictPolygonsMap } from './DistrictPolygonsMap'
import { DistrictsTable } from './DistrictsTable'
import { districtsConfirmLines } from '@/utils/autoScheduleConfirm'
import { toggleDistrictId } from '@/utils/autoScheduleDistricts'
import {
  defaultDistrictsSnapshot,
  hasDistrictsPayload,
  isDistrictsDirty,
  shouldConfirmSubmit,
} from '@/utils/autoScheduleDirty'
import type { TimeInterval } from '@/utils/autoScheduleIntervals'
import { drivesInExactInterval } from '@/utils/autoScheduleRequirements'

const DRIVE_TYPES: DriveType[] = ['statistical', 'evidence', 'documentation', 'testing', 'special']

export function DistrictsMode({
  vehicleId,
  billingPeriodId,
  viewStart,
  viewEnd,
  interval,
  onDirtyChange,
  onFinished,
  mapActive = true,
}: {
  vehicleId: string
  billingPeriodId: number | null
  viewStart: string
  viewEnd: string
  interval: TimeInterval
  onDirtyChange?: (dirty: boolean) => void
  onFinished: () => void
  mapActive?: boolean
}) {
  const { t } = useTranslation(['scheduling', 'common'])
  const queryClient = useQueryClient()
  const { data: districts } = useSuspenseQuery(getDistrictMapOptions())
  const { data: subintervals } = useQuery({
    ...getRequirementSubintervalsOptions({
      path: {
        billing_period_id: billingPeriodId ?? 0,
        starting_from: viewStart,
        ending_at: viewEnd,
      },
    }),
    enabled: billingPeriodId !== null,
  })
  const drives = useMemo(
    () => drivesInExactInterval(subintervals?.drive_subintervals ?? [], interval.start, interval.end),
    [subintervals?.drive_subintervals, interval.start, interval.end]
  )
  const [initial] = useState(defaultDistrictsSnapshot)
  const [selectedDistricts, setSelectedDistricts] = useState<number[]>(initial.districtIds)
  const [hoveredDistrict, setHoveredDistrict] = useState<number>()
  const [driveType, setDriveType] = useState<DriveType[]>(initial.driveType)

  useEffect(() => {
    onDirtyChange?.(isDistrictsDirty({ districtIds: selectedDistricts, driveType }, initial))
  }, [selectedDistricts, driveType, initial, onDirtyChange])

  const { mutate: schedule, isPending } = useMutation({
    ...scheduleDistrictsMutation(),
    onSuccess: data => {
      if (data.scheduled_drives.length === 0) {
        showNotification({ message: t('autoSchedule.noRoutesFound'), color: 'orange' })
        return
      }
      showNotification({
        message: t('autoSchedule.populated', { count: data.scheduled_drives.length }),
        color: 'green',
      })
      invalidateSchedulerQueries(queryClient)
      onFinished()
    },
    onError: () => {
      showNotification({ message: t('autoSchedule.scheduleDistrictsFailed'), color: 'red' })
    },
  })

  const toggle = useCallback((districtId: number) => {
    setSelectedDistricts(current => toggleDistrictId(current, districtId))
  }, [])

  const confirm = () => {
    if (!shouldConfirmSubmit(hasDistrictsPayload(selectedDistricts, driveType))) {
      return
    }
    const lines = districtsConfirmLines({
      vehicleId,
      interval,
      districtIds: selectedDistricts,
      districts,
      driveType,
      labels: {
        vehicle: t('autoSchedule.vehicle'),
        interval: t('autoSchedule.interval'),
        districts: t('autoSchedule.districts'),
        driveTypes: t('driveType'),
      },
    })
    modals.openConfirmModal({
      title: t('autoSchedule.confirmDistrictsTitle'),
      centered: true,
      zIndex: 250,
      children: <ConfirmSummary lines={lines} />,
      labels: { confirm: t('confirm', { ns: 'common' }), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'blue' },
      onConfirm: () =>
        schedule({
          body: {
            vehicle_id: vehicleId,
            district_ids: selectedDistricts,
            start_after: interval.start,
            finish_before: interval.end,
            drive_type: driveType,
          },
        }),
    })
  }

  if (!mapActive) {
    return null
  }

  return (
    <AutoScheduleSplit
      map={
        <DistrictPolygonsMap
          districts={districts}
          selectedIds={selectedDistricts}
          hoveredId={hoveredDistrict}
          onToggle={toggle}
          onHover={setHoveredDistrict}
          drives={drives}
          vehicleId={vehicleId}
        />
      }
      sidebar={
        <>
          <MultiSelect
            label={t('driveType')}
            data={DRIVE_TYPES.map(type => ({ value: type, label: t(`driveTypes.${type}`) }))}
            value={driveType}
            onChange={value => setDriveType(value as DriveType[])}
            style={{ flexShrink: 0 }}
          />
          <DistrictsTable
            districts={districts}
            selectedIds={selectedDistricts}
            hoveredId={hoveredDistrict}
            onToggle={toggle}
            onHover={setHoveredDistrict}
          />
          <Button
            loading={isPending}
            disabled={selectedDistricts.length === 0 || driveType.length === 0}
            onClick={confirm}
            style={{ flexShrink: 0 }}
          >
            {t('confirm', { ns: 'common' })}
          </Button>
        </>
      }
    />
  )
}
